import { gapService } from './gap.service';
import { SecurityGap } from '@/services/assessment.service';

// Gap discovery configuration - fetched from backend
let GAP_DISCOVERY_CONFIG: Record<string, {
  gapId: string;
  stakeholders: string[];
  trustThreshold: number;
  keywords: string[];
}> = {};

// Initialize gap discovery config from backend
async function initializeGapConfig() {
  if (Object.keys(GAP_DISCOVERY_CONFIG).length === 0) {
    GAP_DISCOVERY_CONFIG = await gapService.getGapDiscoveryConfig();
  }
}

// User gap progress tracking
interface UserGapProgress {
  userId: string;
  gapId: string;
  discoveredVia: string[]; // stakeholder IDs who revealed this gap
  discoveredViaNames: string[]; // stakeholder names for display
  quizAttempts: number;
  quizPassed: boolean;
  pointsEarned: number;
  lastAttemptAt?: Date;
}

// Store user progress (in production, this would be in a database)
const userGapProgress: Map<string, UserGapProgress[]> = new Map();

export class GapDiscoveryService {
  /** Hydrate in-memory user gap progress from server */
  static hydrateFromServer(userId: string, gaps: Record<string, Partial<UserGapProgress>>) {
    const list: UserGapProgress[] = Object.keys(gaps || {}).map(gapId => {
      const g = gaps[gapId];
      return {
        userId,
        gapId,
        discoveredVia: g?.discoveredVia || [],
        discoveredViaNames: (g as any)?.discoveredViaNames || [],
        quizAttempts: (g as any)?.quizAttempts || 0,
        quizPassed: !!(g as any)?.quizPassed,
        pointsEarned: (g as any)?.pointsEarned || 0,
        lastAttemptAt: g?.lastAttemptAt ? new Date(g.lastAttemptAt as any) : undefined,
      } as UserGapProgress;
    });
    userGapProgress.set(userId, list);
  }
  /**
   * Check if a message contains triggers for gap discovery
   */
  static async detectGapsInMessage(
    message: string,
    stakeholderId: string,
    userId: string,
    trustLevel: number
  ): Promise<SecurityGap | null> {
    // Initialize gap config from backend if not loaded
    await initializeGapConfig();

    const lowerMessage = message.toLowerCase();

    // Check each gap's configuration
    for (const [gapId, config] of Object.entries(GAP_DISCOVERY_CONFIG)) {
      // Check if this stakeholder can reveal this gap
      if (!config.stakeholders.includes(stakeholderId)) {
        continue;
      }

      // Check trust level requirement
      if (trustLevel < config.trustThreshold) {
        continue;
      }

      // Check if user already discovered this gap from this stakeholder
      const userProgress = this.getUserGapProgress(userId, gapId);
      if (userProgress?.discoveredVia.includes(stakeholderId)) {
        continue;
      }

      // Check if user failed quiz and needs to discover via different stakeholder
      if (userProgress?.quizAttempts >= 3 && !userProgress.quizPassed) {
        // Can only rediscover via different stakeholder
        if (userProgress.discoveredVia.includes(stakeholderId)) {
          continue;
        }
      }

      // Check for keyword triggers
      const hasKeywordTrigger = config.keywords.some(keyword =>
        lowerMessage.includes(keyword.toLowerCase())
      );

      // Determine if gap should be revealed
      if (hasKeywordTrigger && trustLevel >= config.trustThreshold) {
        // Fetch gap from backend
        const gapData = await gapService.getGap(gapId);
        if (gapData) {
          // Transform to SecurityGap format
          const gap: SecurityGap = {
            id: gapData.id,
            title: gapData.title,
            description: gapData.description,
            severity: gapData.severity,
            nistMapping: {
              function: gapData.nistCategory as any,
              category: gapData.nistCategory,
              subcategory: gapData.nistSubcategory,
              description: gapData.description
            },
            identifiedBy: {
              stakeholderId: gapData.personaId,
              stakeholderName: gapData.personaName,
              conversationId: '',
              timestamp: new Date(gapData.createdAt)
            },
            evidence: [],
            remediation: {
              shortTerm: [],
              longTerm: []
            },
            status: 'identified'
          };

          // Record discovery
          this.recordGapDiscovery(userId, gapId, stakeholderId);
          return gap;
        }
      }
    }

    return null;
  }
  
  /**
   * Get user's progress for a specific gap
   */
  static getUserGapProgress(userId: string, gapId: string): UserGapProgress | null {
    const userProgress = userGapProgress.get(userId) || [];
    return userProgress.find(p => p.gapId === gapId) || null;
  }
  
  /**
   * Record that a user discovered a gap via a stakeholder
   */
  static recordGapDiscovery(userId: string, gapId: string, stakeholderId: string): void {
    const userProgress = userGapProgress.get(userId) || [];
    const existingProgress = userProgress.find(p => p.gapId === gapId);
    
    if (existingProgress) {
      if (!existingProgress.discoveredVia.includes(stakeholderId)) {
        existingProgress.discoveredVia.push(stakeholderId);
      }
    } else {
      userProgress.push({
        userId,
        gapId,
        discoveredVia: [stakeholderId],
        discoveredViaNames: [], // Names will be populated from backend
        quizAttempts: 0,
        quizPassed: false,
        pointsEarned: 0
      });
    }
    
    userGapProgress.set(userId, userProgress);
  }
  
  /**
   * Record quiz attempt result
   */
  static recordQuizAttempt(
    userId: string,
    gapId: string,
    passed: boolean,
    pointsEarned: number
  ): void {
    const userProgress = userGapProgress.get(userId) || [];
    const progress = userProgress.find(p => p.gapId === gapId);
    
    if (progress) {
      progress.quizAttempts++;
      progress.quizPassed = passed;
      progress.pointsEarned = Math.max(progress.pointsEarned, pointsEarned);
      progress.lastAttemptAt = new Date();
    }
  }
  
  /**
   * Check if user can attempt quiz for a gap
   */
  static canAttemptQuiz(userId: string, gapId: string): boolean {
    const progress = this.getUserGapProgress(userId, gapId);
    if (!progress) return false;
    
    // Already passed
    if (progress.quizPassed) return false;
    
    // Max attempts reached
    if (progress.quizAttempts >= 3) return false;
    
    return true;
  }
  
  /**
   * Get available stakeholders for gap re-discovery
   */
  static async getAvailableStakeholdersForRediscovery(userId: string, gapId: string): Promise<string[]> {
    await initializeGapConfig();

    const progress = this.getUserGapProgress(userId, gapId);
    if (!progress || progress.quizPassed || progress.quizAttempts < 3) {
      return [];
    }

    const config = GAP_DISCOVERY_CONFIG[gapId];
    if (!config) return [];

    // Return stakeholders who haven't revealed this gap yet
    return config.stakeholders.filter(s => !progress.discoveredVia.includes(s));
  }
  
  /**
   * Get all discovered gaps for a user
   */
  static getUserDiscoveredGaps(userId: string): string[] {
    const userProgress = userGapProgress.get(userId) || [];
    return userProgress.map(p => p.gapId);
  }
  
  /**
   * Get total points earned by user
   */
  static getUserTotalPoints(userId: string): number {
    const userProgress = userGapProgress.get(userId) || [];
    return userProgress.reduce((total, p) => total + p.pointsEarned, 0);
  }

  /**
   * Clear all gap discovery progress for a user (e.g., on reset conversation/session)
   */
  static clearUserProgress(userId: string): void {
    userGapProgress.delete(userId);
  }

  /**
   * Clear gap discovery progress for a specific stakeholder only
   * Used when resetting a single stakeholder session to preserve other stakeholder progress
   */
  static clearProgressForStakeholder(userId: string, stakeholderId: string): void {
    const userProgress = userGapProgress.get(userId) || [];
    // Filter out gaps that were discovered via this stakeholder
    const filtered = userProgress.filter(p =>
      !p.discoveredVia.includes(stakeholderId)
    );
    userGapProgress.set(userId, filtered);
  }
}
